import React, { useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { UsersIcon, TrendingUpIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
type PanelDirection = 'left' | 'right';
interface Panel {
  number: string;
  titleKey: string;
  subtitleKey: string;
  descriptionKey: string;
  bg: string;
  textColor: string;
  numColor: string;
  accentColor: string;
  borderColor: string;
  Icon: ComponentType<{
    className?: string;
  }>;
  direction: PanelDirection;
  pattern: string;
}
const panels: Panel[] = [{
  number: '01',
  titleKey: 'mission.panel1.title',
  subtitleKey: 'mission.panel1.subtitle',
  descriptionKey: 'mission.panel1.description',
  bg: 'bg-haa-blue',
  textColor: 'text-white',
  numColor: 'text-white/20',
  accentColor: 'text-haa-skyblue',
  borderColor: 'border-white',
  Icon: UsersIcon,
  direction: 'left',
  pattern: 'from-haa-blue to-blue-900'
}, {
  number: '02',
  titleKey: 'mission.panel2.title',
  subtitleKey: 'mission.panel2.subtitle',
  descriptionKey: 'mission.panel2.description',
  bg: 'bg-haa-midblue',
  textColor: 'text-white',
  numColor: 'text-white/20',
  accentColor: 'text-white',
  borderColor: 'border-white',
  Icon: TrendingUpIcon,
  direction: 'right',
  pattern: 'from-haa-midblue to-haa-navy'
}, {
  number: '03',
  titleKey: 'mission.panel3.title',
  subtitleKey: 'mission.panel3.subtitle',
  descriptionKey: 'mission.panel3.description',
  bg: 'bg-haa-navy',
  textColor: 'text-white',
  numColor: 'text-white/10',
  accentColor: 'text-haa-skyblue',
  borderColor: 'border-haa-skyblue',
  Icon: SparklesIcon,
  direction: 'left',
  pattern: 'from-haa-navy to-blue-950'
}];
function MissionPanel({
  panel,
  index
}: {
  panel: Panel;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  return <motion.div ref={ref} initial={{
    opacity: 0,
    x: panel.direction === 'left' ? -80 : 80
  }} animate={inView ? {
    opacity: 1,
    x: 0
  } : {}} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1],
    delay: index * 0.1
  }} className={`relative overflow-hidden ${panel.bg}`} data-id="element-1894">
      <div className={`absolute inset-0 bg-gradient-to-br ${panel.pattern} opacity-80`} data-id="element-1895" />
      <div className={`absolute right-0 top-1/2 -translate-y-1/2 font-bebas text-[20rem] leading-none ${panel.numColor} select-none pointer-events-none`} aria-hidden="true" data-id="element-1896">
        {panel.number}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28" data-id="element-1897">
        <div className={`max-w-2xl ${panel.direction === 'right' ? 'ml-auto' : ''}`} data-id="element-1898">
          <motion.div initial={{
          scale: 0,
          rotate: -20
        }} animate={inView ? {
          scale: 1,
          rotate: 0
        } : {}} transition={{
          duration: 0.5,
          delay: index * 0.1 + 0.3
        }} className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl border-2 ${panel.borderColor} mb-6`} data-id="element-1899">
            <panel.Icon className={`w-8 h-8 ${panel.textColor}`} data-id="element-1900" />
          </motion.div>

          <div className={`font-bebas text-6xl sm:text-7xl ${panel.accentColor} leading-none mb-2 tracking-wider`} data-id="element-1901">
            {panel.number}
          </div>

          <h2 className={`font-bebas text-5xl sm:text-6xl lg:text-7xl ${panel.textColor} leading-none tracking-wider mb-2`} data-id="element-1902">
            {t(panel.titleKey)}
          </h2>

          <p className={`font-nunito italic text-lg ${panel.textColor} opacity-80 mb-6`} data-id="element-1903">
            {t(panel.subtitleKey)}
          </p>

          <div className={`w-16 h-1 ${panel.direction === 'left' ? 'bg-haa-yellow' : 'bg-haa-yellow'} rounded-full mb-6`} data-id="element-1904" />

          <p className={`font-nunito text-lg leading-relaxed ${panel.textColor} opacity-90 max-w-lg`} data-id="element-1905">
            {t(panel.descriptionKey)}
          </p>
        </div>
      </div>
    </motion.div>;
}
export function Mission() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    t
  } = useLanguage();
  return <section id="mission" aria-labelledby="mission-heading" data-id="element-1906">
      {/* Section Header */}
      <div ref={titleRef} className="bg-haa-navy py-16 text-center" data-id="element-1907">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-1908">
          <span className="font-nunito text-haa-skyblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-1909">
            {t('mission.label')}
          </span>
          <h2 id="mission-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-white tracking-wider mt-2" data-id="element-1910">
            {t('mission.heading')}
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4" data-id="element-1911">
            <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-1912" />
            <div className="w-3 h-3 rounded-full bg-white" data-id="element-1913" />
            <div className="w-12 h-1 bg-haa-skyblue rounded-full" data-id="element-1914" />
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="font-nunito text-white/80 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto mt-8 px-4" data-id="element-1915">
            {t('mission.statement')}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          scaleX: 0
        }} animate={titleInView ? {
          opacity: 1,
          scaleX: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="flex items-center justify-center gap-3 mt-10" data-id="element-1916">
            <div className="w-12 h-px bg-haa-skyblue/40 rounded-full" data-id="element-1917" />
            <div className="w-2 h-2 rounded-full bg-haa-skyblue/60" data-id="element-1918" />
            <div className="w-12 h-px bg-haa-skyblue/40 rounded-full" data-id="element-1919" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="mt-8 px-4" data-id="element-1920">
            <div className="bg-haa-blue/40 border border-haa-skyblue/30 rounded-2xl px-8 py-8 max-w-3xl mx-auto" data-id="element-1921">
              <span className="font-nunito text-haa-skyblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-1922">
                {t('mission.vision.label')}
              </span>
              <h3 className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-white tracking-wider mt-2 mb-4" data-id="element-1923">
                {t('mission.vision.heading')}
              </h3>
              <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1924">
                <div className="w-12 h-1 bg-haa-skyblue rounded-full" data-id="element-1925" />
                <div className="w-3 h-3 rounded-full bg-white" data-id="element-1926" />
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-1927" />
              </div>
              <p className="font-nunito text-white/80 text-lg sm:text-xl leading-relaxed" data-id="element-1928">
                {t('mission.vision.text')}
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Panels */}
      {panels.map((panel, i) => <MissionPanel key={panel.number} panel={panel} index={i} data-id="element-1929" />)}
    </section>;
}